import { Auth } from '@core/auth';

import ReportApi from '@packages/reports/api';

import { yearsAndMonths } from '@utils/date';

import clock from '@plugins/dayjs';
import i18n from '@plugins/i18n';

import { ReportCollection } from '@/models';

export class ReportVctMaturityCollection extends ReportCollection {
    static type = 'ReportVctMaturityCollection';
    static endpoint = new ReportApi().methods.getVctMaturity;

    static fields() {
        return {
            ...super.fields(),
            adviser_id: this.string(null).nullable(),
            investor_id: this.string(null).nullable()
        };
    }

    get preset_filters() {
        let filters = {};

        if (this.adviser_id) {
            filters.adviser_id = this.filterOperator('is') + ':' + this.adviser_id;
        }

        if (this.investor_id) {
            filters.investor_id = this.filterOperator('is') + ':' + this.investor_id;
        }

        return filters;
    }

    get money_format_options() {
        return {
            decimals: 2,
            abbreviate: false
        };
    }

    get headers() {
        let headers = [
            this.createHeader('product', i18n.t('name'), {
                width: '18rem',
                minWidth: '18rem',
                sortable: true,
                filterable: true
            }),
            this.createHeader('investor', i18n.t('investor'), {
                width: '14rem',
                minWidth: '14rem',
                sortable: true,
                filterable: false,
                active: !this.investor_id && Auth().is_adviser
            }),
            this.createHeader('adviser', i18n.t('adviser'), {
                width: '14rem',
                minWidth: '14rem',
                sortable: true,
                filterable: false,
                active: !this.adviser_id && Auth().is_gi
            }),
            this.createHeader('in_custody', i18n.t('in_custody'), {
                format: 'boolean',
                width: '7rem',
                filterable: true
            }),
            this.createHeader('purchase_cost', i18n.t('purchase_cost'), {
                format: 'money',
                formatOptions: this.money_format_options,
                width: '10rem',
                filterable: true
            }),
            this.createHeader('duration_of_holding', i18n.t('duration_of_holding'), {
                width: '12rem',
                transform: data => {
                    try {
                        const transaction_date = clock(data.item.transaction_date);
                        const share_date = clock(data.item.share_issue_on);
                        return yearsAndMonths(share_date, transaction_date);
                    } catch (error) {
                        return null;
                    }
                },
                sortable: true,
                filterable: {
                    operators: this.getOperators(['lte', 'gte']),
                    component: () => import('@/components/fields/Number'),
                    appendText: 'years'
                }
            }),
            this.createHeader('share_issue_on', i18n.t('share_issue_on'), {
                format: 'date',
                width: '11rem',
                filterable: true
            }),
            this.createHeader('tax_year', i18n.t('tax_year'), {
                width: '11rem',
                transform: data => this.getTaxYear(data.item.tax_year, { abbreviate: true }),
                sortable: true,
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/TaxYear')
                }
            }),
            this.createHeader('share_quantity', i18n.t('share_qty'), {
                format: 'number',
                width: '11rem',
                filterable: true
            }),
            this.createHeader('applicable_tax_relief', i18n.t('applicable_tax_relief'), {
                format: 'money',
                formatOptions: this.money_format_options,
                width: '12rem',
                filterable: true
            }),
            this.createHeader('current_value', i18n.t('current_value'), {
                format: 'money',
                formatOptions: this.money_format_options,
                width: '12rem',
                filterable: true
            }),
            this.createHeader('current_nav', i18n.t('current_nav'), {
                format: 'money',
                formatOptions: this.money_format_options,
                width: '12rem',
                filterable: true
            }),
            this.createHeader('total_return_percentage', i18n.t('total_return_percentage'), {
                column: 'total_return_percentage',
                format: 'percentage',
                formatOptions: this.percentage_format_options,
                width: '10rem',
                sortable: true
            }),

            this.createHeader('sale_requested_at', i18n.t('sale_requested_at'), {
                format: 'datetime',
                width: '11rem',
                filterable: false,
                formatOptions: {
                    showTime: true
                }
            })
        ];

        return headers;
    }

    get default_headers() {
        return [
            'product',
            'investor',
            'adviser',
            'in_custody',
            'purchase_cost',
            'duration_of_holding',
            'share_issue_on',
            'tax_year',
            'share_quantity',
            'applicable_tax_relief',
            'current_value',
            'current_nav',
            'total_return_percentage',
            'sale_requested_at'
        ];
    }

    get investor_headers() {
        return [
            'product',
            'in_custody',
            'purchase_cost',
            'duration_of_holding',
            'share_issue_on',
            'tax_year',
            'share_quantity',
            'applicable_tax_relief',
            'current_value',
            'current_nav',
            'total_return_percentage',
            'sale_requested_at'
        ];
    }
}

export default ReportVctMaturityCollection;
